import { Injectable } from '@angular/core';
import { filterNilValue, Query, QueryEntity } from '@datorama/akita';
import {
    UserSettingsStore,
    ComponentSettingsState,
    RouteSettingsState
} from './user-settings.store';
import {
    ComponentSettings,
    ComponentSettingsKey,
    GlobalSettings,
    GlobalSettingsKey,
    RouteSettings,
    RouteSettingsKey,
    Settings,
    SettingsKey,
    SettingsScope
} from './user-setting.model';
import { Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    pluck,
    shareReplay,
    startWith,
    switchMapTo,
    take
} from 'rxjs/operators';
import { UserSettingsService } from './user-settings.service';
import {
    DEFAULT_GLOBAL_SETTINGS,
    DEFAULT_SETTINGS,
    defaultSetting
} from './user-setting.default';
import { SessionQuery } from 'src/app/core/state/session/session.query';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
type CheckSettingsVersion = 'EXACT' | 'ONLY_MAJOR' | false;
@Injectable({ providedIn: 'root' })
export class UserSettingsQuery extends Query<GlobalSettings> {
    ROUTE: QueryEntity<RouteSettingsState> = new QueryEntity(this.store.ROUTE);
    COMPONENT: QueryEntity<ComponentSettingsState> = new QueryEntity(
        this.store.COMPONENT
    );

    get isLoading$() {
        return this.selectLoading();
    }

    get isLoading() {
        return this.store.getValue().loading;
    }

    /** selectGlobalSettings$ - observable with global settings, (settings stored for global use)
     * @param [fallbackToDefault] - fallback to default settings if no settings are found for a specific key value pair. **Default** value is `true`
     * @param [overrideDefaultValue] - optional object with key value pairs to override the default value of the settings key
     * @param [checkVersion] - check version of settings key, default check is `ONLY_MAJOR`. `ONLY_MAJOR` will return the value if the major version is the same, `EXACT` will return the value if the major and minor version are the same, `false` will return the value regardless of version.
     * @returns Observable with global settings
     */
    selectGlobalSettings$ = (
        fallbackToDefault = true,
        overrideDefaultValue: Partial<{
            [key in GlobalSettingsKey]: GlobalSettings[key]['value'];
        }> = {},
        checkVersion: CheckSettingsVersion = 'ONLY_MAJOR'
    ) =>
        this.select().pipe(
            map((settings) => {
                let defaultSettings = fallbackToDefault
                    ? { ...DEFAULT_GLOBAL_SETTINGS }
                    : {};
                if (Object.keys(overrideDefaultValue).length > 0) {
                    overrideDefaultValue = Object.entries(overrideDefaultValue).reduce(
                        (acc, [key, value]) => {
                            return {
                                ...acc,
                                [key]: { value, version: DEFAULT_SETTINGS[key].version }
                            };
                        },
                        {}
                    );
                    defaultSettings = {
                        ...defaultSettings,
                        ...overrideDefaultValue
                    };
                }
                return Object.entries(settings).reduce(
                    (acc, [key, setting]) => {
                        // only return keys used by this client (app)
                        if (DEFAULT_SETTINGS[key] === undefined) return acc;
                        const newValue = { ...acc };
                        const [CURRENT_MAJOR, CURRENT_MINOR] =
                            DEFAULT_SETTINGS[key].version;
                        const [MAJOR, MINOR] = setting.version ?? [0, 0];
                        let matchVersion = false;
                        switch (checkVersion) {
                            case false:
                                matchVersion = true;
                                break;
                            case 'EXACT':
                                matchVersion =
                                    MAJOR === CURRENT_MAJOR && MINOR === CURRENT_MINOR;
                                break;
                            case 'ONLY_MAJOR':
                            default:
                                matchVersion = MAJOR === CURRENT_MAJOR;
                                break;
                        }

                        if (matchVersion) {
                            newValue[key] = setting;
                        }
                        // TODO: fix return type
                        return newValue;
                    },
                    { ...defaultSettings } as GlobalSettings
                );
            }),
            map((settings) => {
                return Object.entries(settings).reduce((acc, [key, setting]) => {
                    const newValue = { ...acc };
                    newValue[key] = setting.value;
                    return newValue;
                }, {} as { [key in GlobalSettingsKey]: GlobalSettings[key]['value'] });
            }),
            filter((settings) => Object.keys(settings).length > 0)
        );

    /** getGlobalSetting - return global setting for key, (setting stored for global use)
     * @param key - name of settings key to get
     * @param [fallbackToDefault] - if no setting is found for a specific key value pair, fallback to default settings. **Default** value is `true`
     * @param [checkVersion] - check version of settings key, default check is `ONLY_MAJOR`. `ONLY_MAJOR` will return the value if the major version is the same, `EXACT` will return the value if the major and minor version are the same, `false` will return the value regardless of version.
     * @returns value of settings key
     */
    getGlobalSetting = <
        S extends GlobalSettings = GlobalSettings,
        K extends GlobalSettingsKey = GlobalSettingsKey
    >(
        key: K,
        fallbackToDefault = true,
        checkVersion: CheckSettingsVersion = 'ONLY_MAJOR'
    ) => {
        const setting = this.getValue()[key];
        const [CURRENT_MAJOR, CURRENT_MINOR] = DEFAULT_SETTINGS[key].version;
        const [MAJOR, MINOR] = setting?.version ?? [0, 0];
        let matchVersion = false;
        switch (checkVersion) {
            case false:
                matchVersion = true;
                break;
            case 'EXACT':
                matchVersion = MAJOR === CURRENT_MAJOR && MINOR === CURRENT_MINOR;
                break;
            case 'ONLY_MAJOR':
            default:
                matchVersion = MAJOR === CURRENT_MAJOR;
                break;
        }
        if (setting && matchVersion) {
            return setting?.value as S[K]['value'];
        } else {
            return fallbackToDefault ? defaultSetting(key) : null;
        }
    };

    /** getComponentSetting - return component setting for key, (setting stored for component use)
     * @param key - name of settings key to get
     * @param componentId - component id to get setting for
     * @param [fallbackToDefault] - if no setting is found for a specific key value pair, fallback to default settings. **Default** value is `true`
     * @param [checkVersion] - check version of settings key, default check is `ONLY_MAJOR`. `ONLY_MAJOR` will return the value if the major version is the same, `EXACT` will return the value if the major and minor version are the same, `false` will return the value regardless of version.
     * @returns value of settings key
     */
    getComponentSetting = <
        S extends ComponentSettings = ComponentSettings,
        K extends ComponentSettingsKey = ComponentSettingsKey
    >(
        key: K,
        componentId: string,
        fallbackToDefault = true,
        checkVersion: CheckSettingsVersion = 'ONLY_MAJOR'
    ) => {
        const setting = this.COMPONENT.getEntity(key)
            ? this.COMPONENT.getEntity(key)[componentId] ?? false
            : false;
        const [CURRENT_MAJOR, CURRENT_MINOR] = DEFAULT_SETTINGS[key].version;
        const [MAJOR, MINOR] = setting?.version ?? [0, 0];
        let matchVersion = false;
        switch (checkVersion) {
            case false:
                matchVersion = true;
                break;
            case 'EXACT':
                matchVersion = MAJOR === CURRENT_MAJOR && MINOR === CURRENT_MINOR;
                break;
            case 'ONLY_MAJOR':
            default:
                matchVersion = MAJOR === CURRENT_MAJOR;
                break;
        }
        if (setting && matchVersion) {
            return setting?.value as S[K]['value'];
        } else {
            return fallbackToDefault ? defaultSetting(key) : null;
        }
    };

    /** selectGlobalSetting$ - observable for global setting, (setting stored for global use)
     * @param key - name of settings key to select
     * @param [checkVersion] - check version of settings key, default check is `ONLY_MAJOR`. `ONLY_MAJOR` will return the value if the major version is the same, `EXACT` will return the value if the major and minor version are the same, `false` will return the value regardless of version.
     * @returns Observable of the value of the settings key
     */
    selectGlobalSetting$ = <
        S extends GlobalSettings = GlobalSettings,
        K extends GlobalSettingsKey = GlobalSettingsKey
    >(
        key: K,
        checkVersion?: CheckSettingsVersion
    ) =>
        this._selectSettingsForKey$<S[K]['value']>(key, {
            scope: 'GLOBAL',
            checkVersion
        });

    /** selectComponentSetting$ - observable for component setting, (setting stored for specific component)
     * @param key - name of settings key to select
     * @param componentId - id of component to select setting for
     * @param [inherit] - inherit setting from parent level (ROUTE or GLOBAL) if not defined, default is true
     * @param [checkVersion] - check version of settings key, default check is `ONLY_MAJOR`. `ONLY_MAJOR` will return the value if the major version is the same, `EXACT` will return the value if the major and minor version are the same, `false` will return the value regardless of version.
     * @returns Observable of the value of the settings key
     */
    selectComponentSetting$ = <
        S extends ComponentSettings = ComponentSettings,
        K extends ComponentSettingsKey = ComponentSettingsKey
    >(
        key: K,
        componentId: string,
        inherit = true,
        checkVersion?: CheckSettingsVersion
    ) =>
        this._selectSettingsForKey$<S[K]['value']>(key, {
            scope: 'COMPONENT',
            id: componentId,
            inherit,
            checkVersion
        });

    /** selectRouteSetting$ - observable for route setting, (setting stored for specific route e.g. /private-equity)
     * @param key - name of settings key to select
     * @param routeId - id of route to select setting for
     * @param [inherit] - inherit setting from parent level (GLOBAL) if not defined, default is true
     * @param [checkVersion] - check version of settings key, default check is `ONLY_MAJOR`. `ONLY_MAJOR` will return the value if the major version is the same, `EXACT` will return the value if the major and minor version are the same, `false` will return the value regardless of version.
     * @returns Observable of the value of the settings key
     */
    selectRouteSetting$ = <
        S extends RouteSettings = RouteSettings,
        K extends RouteSettingsKey = RouteSettingsKey
    >(
        key: K,
        routeId: string,
        inherit = true,
        checkVersion?: CheckSettingsVersion
    ) =>
        this._selectSettingsForKey$<S[K]['value']>(key, {
            scope: 'ROUTE',
            id: routeId,
            inherit,
            checkVersion
        });

    private _selectSettingsForKey$<T = GlobalSettings[GlobalSettingsKey]>(
        key: GlobalSettingsKey,
        options: {
            scope: 'GLOBAL';
            checkVersion?: CheckSettingsVersion;
        }
    ): Observable<T>;
    private _selectSettingsForKey$<T = RouteSettings[RouteSettingsKey]>(
        key: RouteSettingsKey,
        options: {
            scope: 'ROUTE';
            id?: string;
            inherit?: boolean;
            checkVersion?: CheckSettingsVersion;
        }
    ): Observable<T>;
    private _selectSettingsForKey$<T = ComponentSettings[ComponentSettingsKey]>(
        key: ComponentSettingsKey,
        options: {
            scope: 'COMPONENT';
            id?: string;
            inherit?: boolean;
            checkVersion?: CheckSettingsVersion;
        }
    ): Observable<T>;
    private _selectSettingsForKey$(
        key: SettingsKey,
        options: {
            scope: SettingsScope;
            id?: string;
            inherit?: boolean;
            checkVersion?: CheckSettingsVersion;
        }
    ): Observable<Settings[SettingsKey]['value']> {
        let observable: Observable<Settings[SettingsKey]>;
        let id = options.id;
        const inherit = options.inherit ?? true;
        const checkVersion = options.checkVersion ?? 'ONLY_MAJOR';
        const scope = options.scope;
        switch (scope) {
            case 'GLOBAL':
                observable = this.select(<GlobalSettingsKey>key).pipe(
                    map((setting) => {
                        let appConfig;
                        if (key === 'THEME') {
                            appConfig = this.appConfigQuery.cssTheme?.replace(
                                /-theme/,
                                ''
                            );
                        }
                        return (
                            setting ?? {
                                value: appConfig || DEFAULT_SETTINGS[key].value,
                                version: DEFAULT_SETTINGS[key].version
                            }
                        );
                    })
                );
                break;
            case 'ROUTE':
                if (id) {
                    id = this.router.routerState.snapshot.url.match(/(?!^\/).[^?(]*/)[0];
                }
                if (inherit) {
                    observable = combineLatest([
                        this.ROUTE.selectEntity(key, (entity) => entity[id]).pipe(
                            filterNilValue(),
                            startWith(null)
                        ),
                        this.select(<GlobalSettingsKey>key).pipe(
                            filterNilValue(),
                            startWith(null)
                        )
                    ]).pipe(
                        map(
                            ([route, global]) =>
                                route ||
                                global || {
                                    value: DEFAULT_SETTINGS[key].value,
                                    version: DEFAULT_SETTINGS[key].version
                                }
                        )
                    );
                } else {
                    observable = this.ROUTE.selectEntity(key, (entity) => entity[id]);
                }
                break;
            case 'COMPONENT':
                if (inherit) {
                    const routeId =
                        this.router.routerState.snapshot.url.match(/(?!^\/).[^?(]*/)[0];
                    observable = combineLatest([
                        this.COMPONENT.selectEntity(key, (entity) => entity[id]).pipe(
                            filterNilValue(),
                            startWith(null)
                        ),
                        this.ROUTE.selectEntity(key, (entity) => entity[routeId]).pipe(
                            filterNilValue(),
                            startWith(null)
                        ),
                        this.select(<GlobalSettingsKey>key).pipe(
                            filterNilValue(),
                            startWith(null)
                        )
                    ]).pipe(
                        map(([component, route, global]) => {
                            return (
                                component ||
                                route ||
                                global || {
                                    value: DEFAULT_SETTINGS[key].value,
                                    version: DEFAULT_SETTINGS[key].version
                                }
                            );
                        })
                    );
                } else {
                    observable = this.COMPONENT.selectEntity(key, (entity) => entity[id]);
                }
                break;
        }
        return observable.pipe(
            filter((setting) => {
                if (setting === undefined || setting === null) {
                    return false;
                }
                const [CURRENT_MAJOR, CURRENT_MINOR] = DEFAULT_SETTINGS[key].version;
                const [MAJOR, MINOR] = setting?.version ?? [0, 0];
                switch (checkVersion) {
                    case false:
                        return true;
                    case 'EXACT':
                        return MAJOR === CURRENT_MAJOR && MINOR === CURRENT_MINOR;
                    case 'ONLY_MAJOR':
                    default:
                        return MAJOR === CURRENT_MAJOR;
                }
            }),
            pluck('value'),
            distinctUntilChanged(),
            shareReplay(1)
        );
    }

    constructor(
        protected store: UserSettingsStore,
        private router: Router,
        private userSettingsService: UserSettingsService,
        private appConfigQuery: AppConfigQuery,
        private sessionQuery: SessionQuery
    ) {
        super(store);
        this.COMPONENT.createUIQuery();
        this.ROUTE.createUIQuery();
        this.sessionQuery.isAuthenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                take(1),
                switchMapTo(this.userSettingsService.getAll()),
                take(1)
            )
            .subscribe();
    }
}
